import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { ThemeContext } from 'styled-components/macro'
import { useMediaLayout } from 'use-media'

import CarFileCardPriceBlock, {
  CarFileCardPriceDefinition,
  ExportPriceInfo,
} from 'components/molecules/carfile-card-price-block'
import { formatRoundedPrice, isPrice } from 'utilities/format'

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
`

// A minimum column width keeps the prices in place whatever their value (e.g. "€695" or "€123,456")
const priceColumns = ({ theme }) => `
  > div {
    min-width: ${theme.sizeByFactor(10)};
  }
`

const Columns = styled(CarFileCardPriceBlock)`
  flex-direction: row;
  align-items: center;
  ${priceColumns}
`

// Takes the space of the export price column (including its divider and gaps) when there is no export price
const ExportPricePlaceholder = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizeByFactor(1.5)};
  padding-right: ${({ theme }) => theme.sizeByFactor(1.5)};
  visibility: hidden;
  ${priceColumns}
`

const Divider = styled.span`
  flex-shrink: 0;
  align-self: stretch;
  width: 1px;
  background-color: ${({ theme }) => theme.colors.brandDelta};
`

/**
 * Pricing block of the car file card in the list view: the export price on the
 * left and the selling price always on the right. Without an export price its
 * space stays empty, so the columns line up across cards.
 */
const CarFileCardListPricing = ({
  primaryLabel,
  primaryValue,
  exportPrice,
  exportPriceInclVat,
  className,
  ...restProps
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  // Below the tv breakpoint the filters leave little room, the short label keeps the car title readable
  const isWide = useMediaLayout({ minWidth: theme.metrics.tv })
  const hasExportPrice = isPrice(exportPrice)

  const exportPriceDefinition = (
    <CarFileCardPriceDefinition
      label={isWide ? t('exportPrice') : t('exportPriceShort')}
      value={hasExportPrice ? formatRoundedPrice(exportPrice) : '-'}
      info={<ExportPriceInfo inclVat={exportPriceInclVat} />}
      gap={0.5}
    />
  )

  return (
    <Container className={className} {...restProps}>
      {!hasExportPrice && (
        <ExportPricePlaceholder aria-hidden>
          {exportPriceDefinition}
          <Divider />
        </ExportPricePlaceholder>
      )}
      <Columns>
        {hasExportPrice && (
          <>
            {exportPriceDefinition}
            <Divider />
          </>
        )}
        <CarFileCardPriceDefinition label={primaryLabel} value={primaryValue} />
      </Columns>
    </Container>
  )
}

CarFileCardListPricing.propTypes = {
  primaryLabel: PropTypes.string.isRequired,
  primaryValue: PropTypes.string.isRequired,
  exportPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** True when the export price includes VAT (exportprijs), false for exportprijs_exbtw */
  exportPriceInclVat: PropTypes.bool,
  className: PropTypes.string,
}

CarFileCardListPricing.defaultProps = {
  exportPrice: null,
  exportPriceInclVat: false,
  className: null,
}

export default CarFileCardListPricing
